/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { AxiosResponse } from "axios";

import apiClient from "../../../../services/api-client";
import { error as logError } from "../../../../util/log";
import { getEditPriceReason } from "../../../../util/request/getEditPriceReason";
import { CreateColdOfferPayload, Quote, QuoteProductInfo } from "../types";

export async function createColdOffer(
  payload: CreateColdOfferPayload
): Promise<AxiosResponse<Quote>> {
  // Destructure necessary fields from the payload
  const { accounts, products, note, delivery, isLocked, expiresOn } = payload;

  // Ensure required fields are present
  if (!accounts.length || !products.length || !delivery) {
    throw new Error("Missing required fields");
  }

  try {
    // Set up the request data based on the API contract
    const data: CreateColdOfferPayload = {
      accounts,
      note,
      isLocked,
      expiresOn,
      delivery: delivery,
      products: products.map((product: QuoteProductInfo) => {
        const editedReason: Partial<QuoteProductInfo> =
          getEditPriceReason<QuoteProductInfo>(product);

        return {
          productId: product.productId,
          itemId: product.itemId,
          description: product.description,
          quantity: product.quantity,
          basePriceExcGst: product.originalPriceExcGst
            ? product.originalPriceExcGst
            : product.basePriceExcGst, // need check for 0 price
          discountedPriceExcGst: product.discountedPriceExcGst,
          discountedPriceIncGst: product.discountedPriceIncGst,
          editedPriceExcGst: product.editedPriceExcGst,
          ...editedReason,
        };
      }),
    };

    // Send POST request to create a quote
    const response = await apiClient.post<Quote, CreateColdOfferPayload>("/quotes/offer", data);

    if (!response.status.toString().startsWith("2")) {
      logError("Failed to create quote:", response.statusText);
    }

    return response;
  } catch (error) {
    logError("Error occurred while creating quote:", error);
    throw error;
  }
}
